export type Service = {
  slug: string
  icon: string
  title: string
  shortTitle: string
  description: string
  image: string
  tagline: string
  intro: string
  includes: string[]
  idealFor: string[]
  faqs: { question: string; answer: string }[]
}

const cleaningServices: Service[] = [
  {
    slug: 'commercial-office-cleaning',
    icon: 'building2',
    title: 'Commercial & Office Cleaning',
    shortTitle: 'Commercial & Office',
    description:
      'Regular cleaning for offices, retail premises, communal areas and commercial properties.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5d3a5d11-ff1c-46b3-88b2-379cd496753c.jpg',
    tagline: 'A workspace that makes the right impression, every morning.',
    intro:
      'We keep offices, retail premises and communal areas across Guernsey consistently presentable, with scheduled cleans that work around your business hours. Daily, weekly or fortnightly — you get the same team, the same standards and one point of contact.',
    includes: [
      'Desks, worktops and touch-point sanitising',
      'Kitchens, break-out areas and washrooms',
      'Vacuuming, mopping and hard floor care',
      'Internal glass, partitions and reception areas',
      'Communal stairwells, lifts and entrances',
      'Waste removal and recycling',
      'Consumables restocking on request',
      'Out-of-hours and weekend scheduling',
    ],
    idealFor: ['Offices', 'Retail premises', 'Managed buildings', 'Clinics & studios'],
    faqs: [
      {
        question: 'Can you clean outside of our working hours?',
        answer:
          'Yes. Most of our commercial clients are cleaned early morning, evening or at weekends so there is no disruption to your team or your customers.',
      },
      {
        question: 'Do you supply your own equipment and products?',
        answer:
          'We bring everything needed as standard, including professional-grade products. If you prefer specific eco or fragrance-free products, we are happy to use them.',
      },
      {
        question: 'Can we change our schedule later?',
        answer:
          'Absolutely. Contracts are flexible — you can increase, reduce or pause frequency with reasonable notice.',
      },
    ],
  },
  {
    slug: 'end-of-tenancy-cleaning',
    icon: 'keyRound',
    title: 'End of Tenancy Cleaning',
    shortTitle: 'End of Tenancy',
    description:
      'Thorough cleans for rental handovers and deposit protection.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8a07518b-3252-4993-a6b0-88ede771afa5.jpg',
    tagline: 'Handover-ready properties that protect deposits and re-let faster.',
    intro:
      'A full top-to-bottom clean designed around what letting agents and landlords actually inspect. We work to a detailed checklist so properties pass check-out first time and are ready for the next tenancy without delay.',
    includes: [
      'Full kitchen deep clean including appliances',
      'Oven, hob and extractor degreasing',
      'Bathrooms descaled, sanitised and polished',
      'Inside cupboards, drawers and wardrobes',
      'Skirting boards, doors, frames and switches',
      'Internal windows, sills and tracks',
      'Carpet vacuuming and hard floor cleaning',
      'Cobweb removal and final presentation check',
    ],
    idealFor: ['Landlords', 'Letting agents', 'Outgoing tenants', 'Property managers'],
    faqs: [
      {
        question: 'How long does an end of tenancy clean take?',
        answer:
          'Typically 3–8 hours depending on property size and condition. We confirm timings when we quote so you can plan the check-out.',
      },
      {
        question: 'Do you work to an inventory checklist?',
        answer:
          'Yes. We can work to your agency inventory or our own comprehensive checklist, and we photograph completed work on request.',
      },
      {
        question: 'Can you turn a property around at short notice?',
        answer:
          'Where possible, yes — we hold capacity for urgent handovers and emergency cleans for our property clients.',
      },
    ],
  },
  {
    slug: 'oven-cleaning',
    icon: 'flame',
    title: 'Oven Cleaning',
    shortTitle: 'Oven Cleaning',
    description:
      'Professional deep cleaning to remove grease, burnt-on carbon and cooking residue.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3ac807a2-8ee5-43d3-9547-f54fb8af430d.jpg',
    tagline: 'Grease, carbon and residue removed — inside and out.',
    intro:
      'Ovens are the single most common reason a check-out clean fails. We strip, soak and detail every removable part, restoring the appliance to a genuinely clean condition rather than simply wiping it over.',
    includes: [
      'Racks, trays and panels removed and soaked',
      'Interior carbon and grease removal',
      'Glass doors dismantled and cleaned between panes',
      'Hobs, rings and extractor hoods degreased',
      'Exterior polished and handles detailed',
      'Non-caustic, food-safe products',
      'Hobs, grills, microwaves and ranges covered',
      'Same-visit kitchen wipe-down of affected areas',
    ],
    idealFor: ['Landlords', 'Holiday lets', 'Homeowners', 'Commercial kitchens'],
    faqs: [
      {
        question: 'Are the products safe to use around food?',
        answer:
          'Yes. We use non-caustic, food-safe products with no lingering fumes, so the oven can be used again as soon as we leave.',
      },
      {
        question: 'How long will it take?',
        answer:
          'A single oven is usually 1.5–2.5 hours. Ranges, double ovens and heavily soiled appliances take longer.',
      },
      {
        question: 'Can it be added to another clean?',
        answer:
          'Yes — oven cleaning is most often booked alongside an end of tenancy or deep clean at a combined rate.',
      },
    ],
  },
  {
    slug: 'airbnb-holiday-let-changeovers',
    icon: 'bedDouble',
    title: 'Airbnb & Holiday Let Changeovers',
    shortTitle: 'Holiday Let Changeovers',
    description: 'Changeovers, linen changes and guest-ready inspections.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/750567ed-2f6a-45a3-bf83-e2b1e9c606a9.jpg',
    tagline: 'Five-star arrivals, reliably, between every booking.',
    intro:
      'Same-day changeovers built around your booking calendar. We clean, restyle and inspect the property so every guest walks into the same standard — and you never have to be on the island to manage it.',
    includes: [
      'Full changeover clean between bookings',
      'Bed stripping, linen changes and styling',
      'Fresh towels and bathroom restock',
      'Welcome consumables replenished',
      'Damage, breakage and maintenance reporting',
      'Guest-ready photo inspection on request',
      'Same-day turnarounds and late check-outs',
      'Key handling and holding by arrangement',
    ],
    idealFor: ['Airbnb hosts', 'Holiday let owners', 'Agencies', 'Remote owners'],
    faqs: [
      {
        question: 'Can you handle same-day turnarounds?',
        answer:
          'Yes. Same-day changeovers between a morning check-out and afternoon check-in are our standard for holiday let clients.',
      },
      {
        question: 'Do you supply linen?',
        answer:
          'We can launder your own linen or arrange a hired linen service — whichever works best for your property.',
      },
      {
        question: 'What happens if something is damaged?',
        answer:
          'We report breakages, missing items and maintenance issues with photos the same day, so you can act before your next guest arrives.',
      },
    ],
  },
  {
    slug: 'after-builders-cleaning',
    icon: 'hammer',
    title: 'After Builders Cleaning',
    shortTitle: 'After Builders',
    description: 'New builds, refurbishments and property presentation.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d4a2f9fa-9fa8-4f6d-b459-b8e781a3349b.jpg',
    tagline: 'From building site to handover standard.',
    intro:
      'Construction dust settles everywhere and keeps reappearing. We work in stages — a first sparkle clean followed by a final detail — so new builds and refurbishments are photographed, viewed and handed over looking their best.',
    includes: [
      'Fine construction dust removal throughout',
      'Paint, plaster, adhesive and sticker removal',
      'Window, frame and track cleaning',
      'New kitchen and bathroom detailing',
      'Floor protection removal and floor finishing',
      'Light fittings, sockets and vents',
      'Two-stage builders and sparkle cleans',
      'Snagging-friendly scheduling around trades',
    ],
    idealFor: ['Developers', 'Contractors', 'Architects', 'Renovating owners'],
    faqs: [
      {
        question: 'Do you work around other trades?',
        answer:
          'Yes. We regularly clean in phases alongside snagging and final fit-out, and can attend out of hours to keep programmes on track.',
      },
      {
        question: 'Can you clean large or multi-unit sites?',
        answer:
          'We scale the team to the project, from a single refurbished flat to multi-unit developments with staged handover dates.',
      },
      {
        question: 'Are you insured for site work?',
        answer:
          'We are fully insured and work to site requirements including PPE, induction and access procedures.',
      },
    ],
  },
  {
    slug: 'exterior-cleaning',
    icon: 'droplets',
    title: 'Exterior Cleaning',
    shortTitle: 'Exterior Cleaning',
    description:
      'Window cleaning and pressure washing for a perfect first impression.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a1d47a00-bc12-4884-8ba1-d7abefa4546c.jpg',
    tagline: 'Kerb appeal that lands before anyone steps inside.',
    intro:
      'Windows, frames, patios and pathways cleaned to lift the whole property. Ideal ahead of listing photography, viewings and guest seasons, or on a regular schedule to keep commercial frontages sharp.',
    includes: [
      'Pure water pole window cleaning',
      'Frames, sills and doors included',
      'Conservatory and skylight cleaning',
      'Patio, decking and driveway pressure washing',
      'Path and communal entrance washing',
      'Gutter clearing and fascia cleaning',
      'Pre-photography and pre-viewing cleans',
      'Regular scheduled maintenance rounds',
    ],
    idealFor: ['Estate agents', 'Landlords', 'Businesses', 'Homeowners'],
    faqs: [
      {
        question: 'How often should windows be cleaned?',
        answer:
          'Most residential properties suit every 4–8 weeks. Coastal and commercial frontages usually benefit from a shorter cycle.',
      },
      {
        question: 'Do you need access to the property?',
        answer:
          'Usually not for exterior work — we just need gate and outside tap access where relevant, so you do not have to be home.',
      },
      {
        question: 'Can you clean before a property listing?',
        answer:
          'Yes, and it is one of our most requested jobs. We prioritise pre-photography cleans to fit agency deadlines.',
      },
    ],
  },
]

export const carpetService: Service = {
  slug: 'carpet-upholstery-cleaning',
  icon: 'sofa',
  title: 'Carpet & Upholstery Cleaning',
  shortTitle: 'Carpets & Upholstery',
  description:
    'Deep extraction cleaning for carpets, rugs, sofas and mattresses — stains and odours lifted.',
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/f85db224-38be-40b9-8f41-cb88997faa1a.jpg',
  tagline: 'Carpets and soft furnishings brought back to life.',
  intro:
    'Hot water extraction lifts dirt, stains and odours out of carpets, rugs and upholstery rather than just brushing the surface. Ideal between tenancies, after pets or guests, and for offices and communal areas where footfall shows quickly.',
  includes: [
    'Hot water extraction carpet cleaning',
    'Stain and spot treatment',
    'Pet odour and allergen treatment',
    'Rug cleaning',
    'Sofa, armchair and fabric upholstery cleaning',
    'Mattress deep cleaning and sanitising',
    'Office carpets, stairs and communal areas',
    'Fast-dry methods to minimise downtime',
  ],
  idealFor: ['Landlords', 'Holiday lets', 'Offices', 'Homeowners'],
  faqs: [
    {
      question: 'How long do carpets take to dry?',
      answer:
        'Usually 3–6 hours with good ventilation. We use low-moisture methods where a faster turnaround is needed, such as between tenancies.',
    },
    {
      question: 'Will old stains come out?',
      answer:
        'Most do. We treat stains individually before extraction and will always tell you honestly if a mark is permanent rather than promise a result we cannot deliver.',
    },
    {
      question: 'Can it be combined with an end of tenancy clean?',
      answer:
        'Yes — carpets and upholstery are commonly added to a tenancy clean so the whole property is completed in one visit.',
    },
  ],
}

export const paintingService: Service = {
  slug: 'painting-decorating',
  icon: 'paintRoller',
  title: 'Painting & Decorating',
  shortTitle: 'Painting & Decorating',
  description:
    'Interior redecoration, touch-ups and property refreshes between tenancies and sales.',
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/cb47ae1f-c07e-4f24-89ba-930cdd1b7e87.jpg',
  tagline: 'A fresh coat that resets the whole property.',
  intro:
    'Fresh paint is the quickest way to lift a tired property before a letting, sale or new season. We handle full interior redecoration and smaller between-tenancy touch-ups, working to your deadline and leaving the property clean and ready to hand over.',
  includes: [
    'Full interior painting and redecoration',
    'Between-tenancy touch-ups and scuff repairs',
    'Walls, ceilings, woodwork and doors',
    'Filling, sanding and surface preparation',
    'Communal hallways and stairwells',
    'Colour and finish advice',
    'Furniture and floor protection throughout',
    'Post-decoration clean and handover',
  ],
  idealFor: ['Landlords', 'Estate agents', 'HMO owners', 'Homeowners'],
  faqs: [
    {
      question: 'Can you decorate between tenancies?',
      answer:
        'Yes — void periods are when most of our decorating happens. We coordinate with your cleaning and check-in dates so the property is ready to let on time.',
    },
    {
      question: 'Do you supply the paint?',
      answer:
        'We can supply trade-quality paint or work with a specific brand and colour you have chosen. Materials are itemised clearly in your quote.',
    },
    {
      question: 'Do you handle small jobs?',
      answer:
        'We do. A single room, a hallway or making good after maintenance work are all welcome alongside larger redecorations.',
    },
  ],
}

export const propertyCareService: Service = {
  slug: 'property-care',
  icon: 'clipboardCheck',
  title: 'Property Care',
  shortTitle: 'Property Care',
  description:
    'Inspections, reports, inventories and key holding — eyes on your property when you cannot be there.',
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/1c37df42-c118-4993-bd80-422cb7a90251.jpg',
  tagline: 'Eyes on your property, documented and reported.',
  intro:
    'Beyond cleaning, we look after the property itself. Documented inspections, photographic reports and inventories give landlords, agents and off-island owners a clear, dated record of condition — plus a trusted local point of contact for access, visits and maintenance issues.',
  includes: [
    'Property inspections',
    'Property condition reports',
    'Inventories',
    'Check-in reports',
    'Check-out reports',
    'Property photographic reports',
    'Vacant property checks',
    'Key holding & property access',
    'Landlord property visits',
    'Maintenance issue reporting',
    'Property management support',
    'Estate agent property support',
  ],
  idealFor: ['Landlords', 'Estate agents', 'Off-island owners', 'Property managers'],
  faqs: [
    {
      question: 'What do your reports include?',
      answer:
        'Dated, room-by-room notes with photographs covering condition, cleanliness, meter readings and any issues found — supplied as a clear document you can share with tenants or owners.',
    },
    {
      question: 'Can you hold keys and provide access?',
      answer:
        'Yes. We securely hold labelled keys and can provide access for contractors, viewings and deliveries by arrangement.',
    },
    {
      question: 'How often can vacant properties be checked?',
      answer:
        'Weekly, fortnightly or monthly — whatever your insurer or mortgage terms require. Each visit is logged and reported.',
    },
  ],
}

export const hmoService: Service = {
  slug: 'hmo-management-support',
  icon: 'doorOpen',
  title: 'HMO Management & Support',
  shortTitle: 'HMO Management',
  description:
    'Communal cleaning, room changeovers, inspections and day-to-day support for shared houses.',
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/ee9ff6cf-c617-47ff-83f6-80a9d9dd937e.jpg',
  tagline: 'Shared houses kept clean, compliant and running smoothly.',
  intro:
    'HMOs need constant attention — high tenant turnover, shared spaces and frequent maintenance. We handle the regular communal cleaning, room changeovers and inspections, coordinate contractors and keep landlords informed, so shared properties stay let and well maintained.',
  includes: [
    'HMO property inspections',
    'Regular communal cleaning',
    'Room changeovers',
    'Void room preparation',
    'Maintenance coordination',
    'Contractor access',
    'Tenant issue reporting',
    'Property condition monitoring',
    'Landlord reporting',
    'Emergency property support',
    'General day-to-day HMO management',
  ],
  idealFor: ['HMO landlords', 'Letting agents', 'Property managers', 'Investors'],
  faqs: [
    {
      question: 'How quickly can you turn a room around?',
      answer:
        'Void rooms are usually cleaned and prepared within 24–48 hours of a tenant leaving, so the room can be re-let without losing rent.',
    },
    {
      question: 'Do you deal with tenants directly?',
      answer:
        'We can be the day-to-day point of contact for cleaning and maintenance issues, reporting everything back to you with photos and notes.',
    },
    {
      question: 'Can you coordinate contractors?',
      answer:
        'Yes. We arrange access, meet contractors on site and confirm work is completed to standard before signing it off.',
    },
  ],
}

export const services: Service[] = [
  ...cleaningServices,
  carpetService,
  paintingService,
  propertyCareService,
  hmoService,
]

export function getServiceBySlug(slug: string | undefined): Service | undefined {
  return services.find((service) => service.slug === slug)
}
